'use client'

import Link from 'next/link'
import { SquareSplitHorizontal, ExternalLink } from 'lucide-react'
import type { InspirationGalleryItem } from '@/types/inspiration-gallery'
import { Media } from '@/components/Media'

interface _ProjectImageSource {
  id: string
  title: string
  url: string
  alt: string
  projectSlug: string
}

interface InspirationCardProps {
  item: InspirationGalleryItem
  onGalleryOpen?: (item: InspirationGalleryItem) => void
}

/**
 * InspirationCard Component
 * Displays a single inspiration gallery item with:
 * - Thumbnail preview
 * - Before/After badge (if applicable)
 * - Gallery icon on hover
 * - Source-specific metadata (project link, etc.)
 */
export default function InspirationCard({ item, onGalleryOpen }: InspirationCardProps) {
  const thumbnail = item.media.variant === 'image' ? item.media.image : item.media.after

  const isProjectImage =
    item.source === 'project' && item.sourceMetadata && 'projectSlug' in item.sourceMetadata

  return (
    <div
      className="relative h-[320px] w-full overflow-hidden cursor-pointer group"
      onClick={() => onGalleryOpen?.(item)}
    >
      {/* Background Image */}
      <div className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-[1.04]">
        {thumbnail?.resource && (
          <Media
            resource={thumbnail.resource}
            fill
            size="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
            pictureClassName="absolute inset-0"
            imgClassName="object-cover"
            alt={thumbnail.alt || item.title}
          />
        )}
      </div>

      {/* Before/After Badge - bottom right corner */}
      {item.media.variant === 'beforeAfter' && (
        <div className="absolute bottom-0 right-0 z-20">
          <div className="w-0 h-0 border-l-[40px] border-l-transparent border-b-[40px] border-b-black/80" />
          <div className="absolute bottom-1 right-1 text-white">
            <SquareSplitHorizontal size={16} />
          </div>
        </div>
      )}

      {/* Project Source Badge - bottom right corner with triangle background.
          aria-label gives screen readers and text-only crawlers something
          meaningful — without it the icon-only anchor surfaces the raw href
          as link text in SEO audit tools. */}
      {isProjectImage && (
        <Link
          href={`/projekte/${(item.sourceMetadata as any)?.projectSlug || ''}`}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Projekt ansehen: ${item.title}`}
          className="absolute bottom-0 right-0 z-20"
        >
          <div className="w-0 h-0 border-l-[40px] border-l-transparent border-b-[40px] border-b-brand hover:border-b-brand/80 transition-all duration-200" />
          <div className="absolute bottom-1 right-1 text-white hover:scale-110 transition-transform duration-200">
            <ExternalLink size={16} aria-hidden="true" />
          </div>
        </Link>
      )}

      {/* Gallery View Icon - centered and appears on hover */}
      <div
        className="absolute inset-0 flex items-center justify-center z-10 opacity-0 scale-75 transition-all duration-200 ease-out group-hover:opacity-100 group-hover:scale-100"
      >
        <div className="bg-black/50 rounded-full p-4 backdrop-blur-xs">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-white"
          >
            <rect x="3" y="3" width="6" height="6" rx="1" fill="currentColor" />
            <rect x="15" y="3" width="6" height="6" rx="1" fill="currentColor" />
            <rect x="3" y="15" width="6" height="6" rx="1" fill="currentColor" />
            <rect x="15" y="15" width="6" height="6" rx="1" fill="currentColor" />
          </svg>
        </div>
      </div>
    </div>
  )
}
